// Service detail data — used on dedicated /services/* pages
// Each entry powers a full SEO-friendly page with hero, sections, and CTA.

export const SERVICES = {
  'cultural-needs-assessment': {
    number: '01',
    title: 'Cultural Needs Assessment',
    seoTitle: 'Cultural Needs Assessment | AfriCasa Behavioral Health',
    eyebrow: 'Services',
    calendlyUrl:
      'https://calendly.com/africasabehavioralhealth-info/cultural-needs-assessment-consulting-call',
    shortDescription:
      "We conduct rapid data driven assessments that identify your organization's cultural competency gaps and deliver a clear actionable roadmap for serving refugee and immigrant communities with greater precision and effectiveness.",
    intro:
      "A Cultural Needs Assessment is a rapid data driven process that helps your organization understand exactly where your cultural competency gaps are and what needs to change to effectively serve refugee and immigrant communities.",
    sections: [
      {
        kind: 'block',
        title: 'What It Is',
        text:
          "A Cultural Needs Assessment is a rapid data driven process that helps your organization understand exactly where your cultural competency gaps are and what needs to change to effectively serve refugee and immigrant communities. We gather qualitative and quantitative data from your staff, leadership, programs, and community members to produce a comprehensive report with clear actionable recommendations your organization can implement immediately.",
      },
      {
        kind: 'list',
        title: 'What We Do',
        items: [
          'Review of existing programs, policies, and procedures',
          'Staff and leadership interviews and surveys',
          'Community and client feedback collection',
          'Analysis of language access and communication practices',
          'Identification of service gaps and barriers',
          'Delivery of a comprehensive written report with prioritized recommendations and a clear roadmap for improvement',
        ],
      },
      {
        kind: 'list',
        title: 'Who This Is For',
        items: [
          'Nonprofits and community organizations serving refugee and immigrant populations',
          'Hospitals and health systems with diverse patient populations',
          'School districts with significant refugee and immigrant student enrollment',
          'Government contractors seeking to strengthen cultural competency compliance',
          'Any organization that wants to understand how well they are truly serving the communities in their care',
        ],
      },
      {
        kind: 'block',
        title: 'Engagement Length',
        text:
          "A Cultural Needs Assessment typically takes 30 to 60 days from kickoff to final report delivery depending on the size and complexity of your organization.",
      },
    ],
    investment: {
      text:
        "Every organization has unique needs. Investment is customized based on the scope of work, project goals, timeline, and level of support required. Schedule a consultation to discuss your organization's needs and receive a customized proposal and pricing estimate.",
      cta: {
        label: 'Book a Consultation',
        url: 'https://calendly.com/africasabehavioralhealth-info/cultural-needs-assessment-consulting-call',
      },
    },
    ctaIntro:
      "Ready to understand exactly where your organization stands? Schedule a free 30 minute consultation to scope your assessment.",
  },

  'cultural-competency-consulting': {
    number: '02',
    title: 'Cultural Competency Consulting',
    seoTitle: 'Cultural Competency Consulting | AfriCasa Behavioral Health',
    eyebrow: 'Services',
    calendlyUrl:
      'https://calendly.com/africasabehavioralhealth-info/cultural-competency-training-consulting-call',
    shortDescription:
      "We partner with your organization to build the cultural frameworks, staff training, systems, and programs needed to truly serve refugee and immigrant communities with precision and dignity. From audit to implementation we walk alongside your organization every step of the way.",
    intro:
      "Cultural Competency Consulting is a comprehensive organizational transformation process. We go beyond diversity trainings and awareness campaigns to build the actual systems, frameworks, policies, and programs your organization needs to serve refugee and immigrant communities with cultural precision.",
    sections: [
      {
        kind: 'block',
        title: 'What It Is',
        text:
          "Cultural Competency Consulting is a comprehensive organizational transformation process. We go beyond diversity trainings and awareness campaigns to build the actual systems, frameworks, policies, and programs your organization needs to serve refugee and immigrant communities with cultural precision. We work alongside your leadership and staff from assessment through implementation ensuring that cultural competency becomes embedded in every aspect of how your organization operates.",
      },
      {
        kind: 'list',
        title: 'What We Do',
        items: [
          'Comprehensive organizational cultural competency audit',
          'Staff and leadership cultural competency training for clinical and non-clinical teams',
          'Development of culturally responsive policies and procedures',
          'Program design and redesign for refugee and immigrant populations',
          'Trauma-informed care framework development',
          'Family engagement strategy development',
          'Language access policy and interpreter protocol development',
          'Ongoing consulting and quality improvement support',
          'Cultural broker and community liaison integration guidance',
        ],
      },
      {
        kind: 'list',
        title: 'Who This Is For',
        items: [
          'Nonprofits and refugee resettlement agencies',
          'Hospitals and health systems',
          'K-12 school districts',
          'Government contractors and federal agencies',
          'Any organization committed to serving refugee and immigrant communities with precision and dignity',
        ],
      },
      {
        kind: 'block',
        title: 'Engagement Length',
        text:
          "Cultural Competency Consulting engagements typically range from 3 months for targeted organizational training to 6 to 12 months for comprehensive organizational transformation. Ongoing retainer consulting is also available for organizations seeking continuous quality improvement support.",
      },
    ],
    investment: {
      text:
        "Every organization has unique needs. Investment is customized based on the scope of work, project goals, timeline, and level of support required. Schedule a consultation to discuss your organization's needs and receive a customized proposal and pricing estimate.",
      cta: {
        label: 'Book a Consultation',
        url: 'https://calendly.com/africasabehavioralhealth-info/cultural-competency-training-consulting-call',
      },
    },
    ctaIntro:
      "Ready to embed cultural competency throughout your organization? Schedule a free 30 minute consultation to explore the right framework for your team.",
  },

  'case-management': {
    number: '03',
    title: 'Organizational Case Management Services',
    seoTitle: 'Organizational Case Management Services | AfriCasa Behavioral Health',
    eyebrow: 'Services',
    calendlyUrl:
      'https://calendly.com/africasabehavioralhealth-info/organizational-case-management-consulting-call',
    shortDescription:
      'We partner with organizations to strengthen case management systems through culturally responsive care coordination, capacity building, resource navigation, and client advocacy, ensuring individuals and families receive seamless, high-quality support.',
    intro:
      'AfriCasa partners with healthcare organizations, nonprofits, schools, government agencies, and community-based organizations to strengthen case management services and improve client outcomes.',
    sections: [
      {
        kind: 'block',
        title: 'What It Is',
        text:
          "AfriCasa partners with healthcare organizations, nonprofits, schools, government agencies, and community-based organizations to strengthen case management services and improve client outcomes. Whether your organization needs outsourced case management, care coordination, program support, or assistance navigating complex systems for refugee and immigrant populations, we provide culturally responsive solutions that enhance service delivery, improve continuity of care, and strengthen organizational capacity.",
      },
      {
        kind: 'list',
        title: 'What We Do',
        items: [
          'Contracted Case Management Services',
          'Care Coordination',
          'Resource Navigation',
          'Community Outreach',
          'Client Advocacy',
          'Program Support',
          'Capacity Building',
          'Care Transition Support',
          'Staff Consultation',
          'Cross-System Collaboration',
          'Documentation & Reporting Support',
        ],
      },
      {
        kind: 'list',
        title: 'Who This Is For',
        items: [
          'Hospitals & Health Systems',
          'Behavioral Health Organizations',
          'Community Health Centers',
          'Nonprofit Organizations',
          'Refugee Resettlement Agencies',
          'School Districts',
          'Government Agencies',
          'Managed Care Organizations',
          'Community-Based Organizations',
        ],
      },
      {
        kind: 'block',
        title: 'Engagement Length',
        text:
          "Organizational case management services are customized based on each organization's needs and may be delivered through short-term projects, ongoing contracts, program partnerships, or subcontracted support.",
      },
    ],
    investment: {
      text:
        "Every organization has unique needs. Investment is customized based on the scope of work, project goals, timeline, and level of support required. Schedule a consultation to discuss your organization's needs and receive a customized proposal and pricing estimate.",
      cta: {
        label: 'Book a Consultation',
        url: 'https://calendly.com/africasabehavioralhealth-info/organizational-case-management-consulting-call',
      },
    },
    ctaIntro:
      "Ready to strengthen your organization's case management capacity? Schedule a free consultation to discuss your needs.",
  },

  'concierge-case-management': {
    number: '05',
    title: 'Concierge Case Management Services',
    seoTitle: 'Concierge Case Management Services | AfriCasa Behavioral Health',
    eyebrow: 'Services',
    calendlyUrl:
      'https://calendly.com/africasabehavioralhealth-info/concierge-case-management-consulting-call',
    shortDescription:
      'Personalized, one-on-one case management designed to help individuals and families confidently navigate healthcare, behavioral health, housing, education, employment, and community resources through compassionate, culturally responsive support.',
    intro:
      'Concierge Case Management is a personalized support service designed for individuals and families who need expert guidance navigating complex systems and accessing essential resources.',
    sections: [
      {
        kind: 'block',
        title: 'What It Is',
        text:
          "Concierge Case Management is a personalized support service designed for individuals and families who need expert guidance navigating complex systems and accessing essential resources. Whether you're adjusting to life in a new community, coordinating healthcare, applying for benefits, finding housing, accessing behavioral health services, or overcoming barriers to stability, AfriCasa provides individualized support every step of the way. Unlike traditional case management programs with limited availability, our concierge model offers personalized attention, proactive planning, advocacy, and ongoing coordination tailored to each client's unique goals and circumstances.",
      },
      {
        kind: 'list',
        title: 'What We Do',
        items: [
          'Comprehensive Needs Assessment',
          'Healthcare Navigation',
          'Behavioral Health Coordination',
          'Housing & Community Resource Navigation',
          'Benefits Assistance',
          'Education & Employment Support',
          'Appointment Coordination',
          'Advocacy & Systems Navigation',
          'Community Resource Referrals',
          'Personalized Service Planning',
          'Ongoing Follow-Up & Accountability',
        ],
      },
      {
        kind: 'list',
        title: 'Who This Is For',
        items: [
          'Individuals navigating healthcare systems',
          'Individuals seeking behavioral health resource coordination',
          'Immigrants and refugees',
          'Members of the African diaspora',
          'Older adults and caregivers',
          'Families seeking coordinated support',
          'College students and young professionals',
          'Individuals experiencing major life transitions',
          'Anyone needing personalized guidance navigating community resources',
        ],
      },
      {
        kind: 'block',
        title: 'Engagement Length',
        text:
          "Concierge Case Management services are individualized based on each client's needs and may be provided through one-time consultations, short-term support, or ongoing care coordination.",
      },
    ],
    investment: {
      grid: 'two-column',
      intro:
        'Choose the level of Concierge Case Management support that best fits your needs.',
      tiers: [
        {
          name: 'Complimentary Consultation',
          subtitle:
            "Explore whether Concierge Case Management is the right fit for your needs during this complimentary 30-minute consultation. We'll discuss your current situation, answer your questions, and determine the most appropriate level of support for you or your family.",
          price: 'Free',
          cta: {
            label: 'Book Free Consultation',
            url: 'https://calendly.com/africasabehavioralhealth-info/concierge-case-management-consulting-call',
          },
        },
        {
          name: 'Comprehensive Case Management Consultation',
          subtitle:
            "Receive a comprehensive 60-minute consultation designed to help you navigate healthcare, behavioral health, education, and other complex systems. Together, we'll assess your needs, identify barriers, develop an action plan, and connect you with appropriate resources. This service is ideal for individuals and families seeking one-time guidance without ongoing case management.",
          price: '$150',
          cta: {
            label: 'Book a Consultation',
            url: 'https://calendly.com/africasabehavioralhealth-info/comprehensive-case-management-consultation',
          },
        },
        {
          name: 'Ongoing Concierge Case Management',
          subtitle:
            'Receive personalized ongoing case management tailored to your unique needs. Services may include care coordination, resource navigation, advocacy, provider communication, follow-up support, and personalized guidance. Every support plan is customized based on your goals and level of need.',
          price: 'Plans starting at $300',
          priceUnit: '/month',
          cta: {
            label: 'Book Free Consultation',
            url: 'https://calendly.com/africasabehavioralhealth-info/concierge-case-management-consulting-call',
          },
        },
        {
          name: 'Premium Concierge Support',
          subtitle:
            'Designed for individuals and families requiring a higher level of personalized coordination, advocacy, and ongoing support across multiple systems. Premium support plans are customized to provide intensive, high-touch services based on your unique needs.',
          price: 'Plans starting at $600',
          priceUnit: '/month',
          cta: {
            label: 'Book Free Consultation',
            url: 'https://calendly.com/africasabehavioralhealth-info/concierge-case-management-consulting-call',
          },
        },
      ],
      note:
        "Services are personalized based on each client's unique needs. Additional hours may be available upon request.",
    },
    ctaButton: {
      label: 'Book Free Consultation',
      url: 'https://calendly.com/africasabehavioralhealth-info/concierge-case-management-consulting-call',
    },
    ctaHeading: 'Ready to get started?',
    ctaIntro:
      'Schedule a complimentary consultation to discuss your needs and learn how Concierge Case Management can provide personalized guidance, advocacy, and ongoing support.',
  },

  'culturally-responsive-coaching': {
    number: '04',
    title: 'Culturally Responsive Coaching',
    seoTitle: 'Culturally Responsive Coaching | AfriCasa Behavioral Health',
    eyebrow: 'Services',
    calendlyUrl:
      'https://calendly.com/africasabehavioralhealth-info/culturally-responsive-coaching',
    shortDescription:
      'One-on-one culturally responsive coaching that empowers individuals to navigate life transitions, strengthen resilience, achieve personal and professional goals, and thrive across cultures.',
    intro:
      "Culturally Responsive Coaching is a one-on-one coaching service designed specifically for immigrants, refugees, and members of the African diaspora navigating life across cultures.",
    sections: [
      {
        kind: 'block',
        title: 'What It Is',
        text:
          "Culturally Responsive Coaching is a one-on-one coaching service designed specifically for immigrants, refugees, and members of the African diaspora navigating life across cultures. Whether you're adjusting to life in a new country, balancing multiple cultural identities, pursuing career advancement, launching a business, or seeking greater purpose, our coaching provides personalized guidance, accountability, and practical strategies to help you move forward with confidence. Every coaching experience is tailored to each client's unique goals, lived experiences, cultural background, and personal journey. We help clients navigate cultural transitions, strengthen resilience, build confidence, overcome challenges, and achieve meaningful personal and professional growth while honoring their identity and experiences. This is a non-clinical coaching service focused on personal growth, leadership, wellness, resilience, cultural adjustment, and goal achievement. It is not psychotherapy, counseling, or mental health treatment.",
      },
      {
        kind: 'list',
        title: 'What We Do',
        items: [
          'One-on-One Coaching Sessions',
          'Goal Setting & Accountability',
          'Personal Growth & Confidence Building',
          'Career Development',
          'Leadership Coaching',
          'Entrepreneurship Coaching',
          'Life Transition Coaching',
          'Wellness & Resilience Strategies',
          'Work-Life Balance Planning',
          'Cultural Identity & Belonging',
          'Healthy Boundaries',
          'Personalized Action Plans',
        ],
      },
      {
        kind: 'list',
        title: 'Who This Is For',
        items: [
          'Members of the African diaspora',
          'Immigrants and refugees',
          'College students and young professionals',
          'Entrepreneurs and aspiring business owners',
          'Individuals navigating major life transitions',
          'Individuals seeking culturally responsive support for personal growth, confidence, resilience, and life transitions.',
        ],
      },
      {
        kind: 'list',
        title: 'Areas of Focus',
        items: [
          'Cultural Identity & Belonging',
          'Career & Professional Growth',
          'Leadership Development',
          'Entrepreneurship',
          'Life Transitions',
          'Confidence & Mindset',
          'Goal Setting & Accountability',
          'Work-Life Balance',
          'Cultural Adjustment & Integration',
          'Wellness & Resilience',
        ],
      },
      {
        kind: 'block',
        title: 'Engagement Options',
        text:
          "Choose the coaching option that best fits your goals. Services are available as a complimentary 30-minute consultation, an individual 60-minute coaching session, a 4-session coaching package, or an 8-session coaching package. Coaching packages provide ongoing support, accountability, and personalized guidance and must be used within six months of purchase.",
      },
      {
        kind: 'block',
        title: 'Disclaimer',
        text:
          "Culturally Responsive Coaching is a non-clinical coaching service and is not a substitute for psychotherapy, counseling, or mental health treatment. Individuals experiencing significant mental health concerns should seek services from a licensed mental health professional.",
      },
    ],
    investment: {
      grid: 'two-column',
      intro: 'Flexible coaching options designed to meet you where you are.',
      tiers: [
        {
          name: 'Complimentary Consultation',
          subtitle:
            "Explore whether culturally responsive coaching is the right fit for you during this complimentary 30-minute consultation. We'll discuss your goals, current challenges, and how coaching can support your personal and professional growth.",
          price: 'Free',
          cta: {
            label: 'Book Free Consultation',
            url: 'https://calendly.com/africasabehavioralhealth-info/culturally-responsive-coaching-consultation',
          },
        },
        {
          name: 'Individual Coaching Session',
          subtitle:
            'A 60-minute one-on-one culturally responsive coaching session tailored to your unique goals, lived experiences, and cultural background. Receive personalized guidance, practical strategies, and accountability to help you move forward with confidence.',
          price: '$150',
          cta: {
            label: 'Book a Session',
            url: 'https://calendly.com/africasabehavioralhealth-info/culturally-responsive-coaching',
          },
        },
        {
          name: '4-Session Coaching Package',
          popular: true,
          subtitle:
            'Perfect for individuals seeking focused support around a specific goal or life transition. Includes four 60-minute coaching sessions with personalized guidance, accountability, and practical strategies.',
          price: '$560',
          savings: 'Save $40',
          cta: {
            label: 'Purchase 4-Session Package',
            url: 'https://calendly.com/africasabehavioralhealth-info/packages/561dfbeb-294f-4f41-8e72-258d5b0f81ba',
          },
        },
        {
          name: '8-Session Coaching Package',
          subtitle:
            'Designed for individuals seeking deeper, long-term support. Includes eight 60-minute coaching sessions focused on sustainable growth, resilience, accountability, and meaningful personal and professional development.',
          price: '$1,040',
          savings: 'Save $160',
          cta: {
            label: 'Purchase 8-Session Package',
            url: 'https://calendly.com/africasabehavioralhealth-info/packages/6961f56f-4361-4630-a4d1-c02b8c95f066',
          },
        },
      ],
      note: 'Package sessions must be used within six months of purchase.',
    },
    ctaButton: {
      label: 'Book Free Consultation',
      url: 'https://calendly.com/africasabehavioralhealth-info/culturally-responsive-coaching-consultation',
    },
    ctaHeading: 'Ready to take the next step?',
    ctaIntro:
      "Schedule a complimentary consultation to explore whether culturally responsive coaching is the right fit for your personal or professional goals.",
  },
};

export const CALENDLY_URL = 'https://calendly.com/africasabehavioralhealth-info/30min';
